'use client'

import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getClientsByOrganization,
    deleteClient,
    Client,
} from '@/lib/api/clients/client-api'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdSearch,
    MdClose,
    MdPerson,
    MdPhone,
    MdEmail,
    MdLocationOn,
    MdArrowForward,
    MdAdd,
    MdChat,
    MdDelete,
    MdMedicalServices,
    MdHandshake,
    MdFileDownload,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { ShareableModule } from '@/lib/api/share/share-api'
import { formatPhoneNumber } from '@/lib/form-state/util'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useDebounce } from '@/lib/hooks/use-debounce'

const getInitials = (name: string): string => {
    const parts = name.trim().split(' ')
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
    return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase()
}

const getAvatarColor = (name: string): string => {
    const colors = [
        'from-secondary to-secondary/70',
        'from-secondary to-secondary/70',
        'from-accent to-accent/70',
        'from-info to-info/70',
        'from-success to-success/70',
        'from-warning to-warning/70',
        'from-error to-error/70',
    ]
    const index = name.charCodeAt(0) % colors.length
    return colors[index]
}

export default function ClientsPage() {
    const { user } = useAuth()
    const { clientLabel, ClientLabel } = useClientLabel()
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('person', 'clients')
    const [showSearch, setShowSearch] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [showImportModal, setShowImportModal] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<Client | null>(null)
    const [deleting, setDeleting] = useState(false)

    const debouncedSearch = useDebounce(searchInput, 300)

    const organizationId = user?.organizationId

    const fetchFn = useCallback(
        (params: { page: number; limit: number; search?: string }) =>
            getClientsByOrganization(organizationId!, params),
        [organizationId]
    )

    const filters = useMemo(
        () => ({ search: debouncedSearch.trim() || undefined }),
        [debouncedSearch]
    )

    const [urlPage, setUrlPage] = useUrlPage()

    const {
        items: clients,
        loading,
        page,
        limit,
        total,
        setPage,
        reload,
    } = usePaginatedPage<Client, { search?: string }>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!organizationId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const handleDelete = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        try {
            const response = await deleteClient(deleteTarget.id)
            if (response.success) {
                toast.success(`${clientLabel} removido com sucesso`)
                setDeleteTarget(null)
                reload()
            } else {
                toast.error(response.error || `Erro ao remover ${clientLabel}`)
            }
        } catch {
            toast.error(`Erro ao remover ${clientLabel}`)
        } finally {
            setDeleting(false)
        }
    }

    const handleCancelSearch = () => {
        setShowSearch(false)
        setSearchInput('')
    }

    if (loading && clients.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando {clientLabel}s...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdPerson className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            {ClientLabel}s
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {total}{' '}
                            {total === 1 ? clientLabel : `${clientLabel}s`}{' '}
                            cadastrado{total !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {showSearch ? (
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <div className="relative flex-1 sm:flex-initial">
                                <input
                                    type="text"
                                    placeholder={`Buscar ${clientLabel}s...`}
                                    value={searchInput}
                                    onChange={e =>
                                        setSearchInput(e.target.value)
                                    }
                                    className="input input-bordered w-full sm:w-72 pl-10"
                                    autoFocus
                                />
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg" />
                            </div>
                            <button
                                type="button"
                                onClick={handleCancelSearch}
                                className="btn btn-ghost gap-2"
                            >
                                <MdClose className="text-lg" />
                                <span className="hidden sm:inline">
                                    Cancelar
                                </span>
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="btn btn-ghost gap-2"
                            >
                                <MdSearch className="text-lg" />
                                <span className="hidden sm:inline">Buscar</span>
                            </button>
                            <button
                                className="btn btn-outline btn-secondary gap-2"
                                onClick={() => setShowImportModal(true)}
                            >
                                <MdFileDownload className="text-xl" />
                                <span className="hidden sm:inline">
                                    Importar
                                </span>
                            </button>
                            <button
                                onClick={() => navigate('/person/clients/new')}
                                className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                            >
                                <MdAdd className="text-lg" />
                                <span className="hidden sm:inline">
                                    Novo {ClientLabel}
                                </span>
                            </button>
                        </>
                    )}
                </div>
            </div>

            {clients.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdPerson className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhum {clientLabel} encontrado
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        {debouncedSearch
                            ? 'Tente buscar com outros termos ou limpe a busca para ver todos os clientes.'
                            : `Comece adicionando seu primeiro ${clientLabel} ao sistema.`}
                    </p>
                    {!debouncedSearch && canCreate && (
                        <button
                            onClick={() => navigate('/person/clients/new')}
                            className="btn btn-secondary btn-lg gap-2 shadow-lg shadow-secondary/20"
                        >
                            <MdAdd className="text-xl" />
                            Adicionar {ClientLabel}
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                        {clients.map(client => (
                            <div
                                key={client.id}
                                onClick={() =>
                                    navigate(`/person/clients/${client.id}`)
                                }
                                className="group bg-gradient-to-br from-base-100 to-base-200/20 rounded-2xl border border-base-300/50 shadow-sm hover:shadow-xl hover:border-secondary/30 transition-all duration-300 cursor-pointer overflow-hidden relative"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                                <div className="relative p-6">
                                    <div className="flex items-start gap-4 mb-5">
                                        <div
                                            className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getAvatarColor(client.name)} flex items-center justify-center shadow-lg flex-shrink-0`}
                                        >
                                            <span className="text-2xl font-bold text-white">
                                                {getInitials(client.name)}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xl font-bold text-base-content mb-2 group-hover:text-secondary transition-colors line-clamp-2">
                                                {client.name}
                                            </h3>
                                            {client.email && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70 mb-1.5">
                                                    <MdEmail className="text-base text-secondary/60 flex-shrink-0" />
                                                    <span className="truncate">
                                                        {client.email}
                                                    </span>
                                                </div>
                                            )}
                                            {client.whatsAppNumber && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                    <MdPhone className="text-base text-success/60 flex-shrink-0" />
                                                    <span className="font-medium">
                                                        {formatPhoneNumber(
                                                            client.whatsAppNumber
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-shrink-0 flex items-center gap-1">
                                            <button
                                                type="button"
                                                title={`Excluir ${clientLabel}`}
                                                className="w-8 h-8 rounded-lg bg-base-200/50 hover:bg-error/10 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    setDeleteTarget(client)
                                                }}
                                            >
                                                <MdDelete className="text-base-content/40 hover:text-error transition-colors" />
                                            </button>
                                            <div className="w-8 h-8 rounded-lg bg-base-200/50 group-hover:bg-secondary/10 flex items-center justify-center transition-colors">
                                                <MdArrowForward className="text-base-content/40 group-hover:text-secondary transition-colors" />
                                            </div>
                                        </div>
                                    </div>

                                    {client.partner && (
                                        <div className="flex items-center gap-2 text-sm text-base-content/70 mb-2 px-3 py-2 bg-warning/5 rounded-lg border border-warning/20">
                                            <MdHandshake className="text-base text-warning/70 flex-shrink-0" />
                                            <span className="truncate font-medium">
                                                {client.partner.name ||
                                                    client.partner.tradingName}
                                            </span>
                                        </div>
                                    )}

                                    {client.professional && (
                                        <div className="flex items-center gap-2 text-sm text-base-content/70 mb-2 px-3 py-2 bg-secondary/5 rounded-lg border border-secondary/20">
                                            <MdMedicalServices className="text-base text-secondary/70 flex-shrink-0" />
                                            <span className="truncate font-medium">
                                                {client.professional.name}
                                            </span>
                                        </div>
                                    )}

                                    {(client.city || client.state) && (
                                        <div className="flex items-center gap-2 text-sm text-base-content/60 mb-4 px-3 py-2 bg-base-200/30 rounded-lg">
                                            <MdLocationOn className="text-base text-info/60 flex-shrink-0" />
                                            <span className="truncate">
                                                {[client.city, client.state]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </span>
                                        </div>
                                    )}

                                    <div className="pt-4 border-t border-base-300/50">
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/10 rounded-lg">
                                                    <MdChat className="text-base text-secondary" />
                                                    <span className="text-sm font-semibold text-base-content">
                                                        {client.chatsCount || 0}
                                                    </span>
                                                    <span className="text-xs text-base-content/60">
                                                        {(client.chatsCount ||
                                                            0) === 1
                                                            ? 'atendimento'
                                                            : 'atendimentos'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-xs text-base-content/50 text-right">
                                                {format(
                                                    new Date(client.createdAt),
                                                    'dd/MM/yyyy',
                                                    { locale: ptBR }
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label={clientLabel.toLowerCase()}
                        />
                    )}
                </>
            )}
            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title={`Excluir ${clientLabel}`}
                message={`Tem certeza que deseja excluir "${deleteTarget?.name}"? Esta ação não pode ser desfeita.`}
                loading={deleting}
                confirmButtonText="Excluir"
                confirmButtonClass="btn-error"
            />
            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.CLIENT}
                moduleLabel={`${ClientLabel}s`}
                fetchPaginated={async (companyId, params) =>
                    getClientsByOrganization(companyId, params)
                }
                renderItem={(item: Client) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.name}
                        </p>
                        <p className="text-xs text-base-content/60 truncate">
                            {[
                                item.email,
                                item.whatsAppNumber
                                    ? formatPhoneNumber(item.whatsAppNumber)
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(' - ') || 'Sem contato'}
                        </p>
                    </div>
                )}
                getItemId={(item: Client) => item.id}
                getItemName={(item: Client) => item.name}
                onImportComplete={reload}
            />
        </div>
    )
}
